import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { saveProjectReview } from "apps/project-iq/api";
import { ITeamReviewEntry } from "models/IProjectIQWorkspace";
import { RootState } from "store";
import {
  selectAvailableProjectIqWorkspaces,
  updateProjectIqWorkspace as updateWorkspaceAction,
} from "redux/projectIqWorkspacesSlice";
import {
  SAVE_DEBOUNCE_MS,
  SAVE_IDLE_DELAY_MS,
} from "./ProjectReview.constants";
import { useProjectReviewStyles } from "./ProjectReview.styles";
import {
  IProjectReviewProps,
  ProjectReviewSaveStatus,
} from "./ProjectReview.types";
import { ReviewStatusDashboard } from "./ReviewStatusDashboard";
import { TeamReviewTracker } from "./TeamReviewTracker";
import { IReviewRow } from "./TeamReviewTracker.types";

const SCHEDULE_RAG_DIMENSION_ID = "schedule";
const ISO_DATE_LENGTH = 10;

const createDefaultEntry = (teamRoleId: string): ITeamReviewEntry => ({
  teamRoleId,
  reviewer: "",
  status: "not_started",
  completedDate: null,
});

export const ProjectReview = ({
  workspaceIdentifier,
  onSaveStatusChange,
}: IProjectReviewProps): JSX.Element => {
  const styles = useProjectReviewStyles();
  const dispatch = useDispatch();
  const workspaces = useSelector(selectAvailableProjectIqWorkspaces);
  const workspace = workspaces.find((w) => w.id === workspaceIdentifier);
  const currentUser = useSelector(
    (state: RootState) => state.currentUserSlice.value,
  );
  const canEdit =
    workspace?.owners?.some((owner) => owner.userId === currentUser?.id) ??
    false;

  const projectEndDate = workspace?.projectPlan?.ragDimensions?.find(
    (dimension) => dimension.id === SCHEDULE_RAG_DIMENSION_ID,
  )?.endDate;

  const teamRoles = useMemo(
    () => workspace?.people?.teamRoles ?? [],
    [workspace?.people?.teamRoles],
  );

  const [entries, setEntries] = useState<ITeamReviewEntry[]>(
    workspace?.projectReview?.entries ?? [],
  );
  const [saveStatus, setSaveStatus] = useState<ProjectReviewSaveStatus>("idle");
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    onSaveStatusChange?.(saveStatus);
  }, [saveStatus, onSaveStatusChange]);

  useEffect(() => {
    setEntries(workspace?.projectReview?.entries ?? []);
  }, [workspace?.id]);

  const rows = useMemo<IReviewRow[]>(
    () =>
      teamRoles.map((teamRole) => {
        const entry =
          entries.find((candidate) => candidate.teamRoleId === teamRole.id) ??
          createDefaultEntry(teamRole.id);
        return {
          teamRoleId: teamRole.id,
          memberName: teamRole.assignedMember,
          roleName: teamRole.name,
          reviewer: entry.reviewer,
          status: entry.status,
          completedDate: entry.completedDate,
        };
      }),
    [teamRoles, entries],
  );

  const save = async (next: ITeamReviewEntry[]) => {
    if (!workspace || !canEdit) return;
    const teamRoleIds = new Set(teamRoles.map((teamRole) => teamRole.id));
    const prunedEntries = next.filter((entry) =>
      teamRoleIds.has(entry.teamRoleId),
    );
    setSaveStatus("saving");
    try {
      await saveProjectReview({
        id: workspaceIdentifier,
        projectReview: { entries: prunedEntries },
      });
      dispatch(
        updateWorkspaceAction({
          ...workspace,
          projectReview: { entries: prunedEntries },
        }),
      );
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), SAVE_IDLE_DELAY_MS);
    } catch {
      setSaveStatus("error");
    }
  };

  const scheduleSave = (next: ITeamReviewEntry[]) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => void save(next), SAVE_DEBOUNCE_MS);
  };

  const handleEntryChange = (
    teamRoleId: string,
    patch: Partial<ITeamReviewEntry>,
  ) => {
    const current =
      entries.find((entry) => entry.teamRoleId === teamRoleId) ??
      createDefaultEntry(teamRoleId);
    let nextEntry: ITeamReviewEntry = { ...current, ...patch };

    if (patch.status === "complete" && current.status !== "complete") {
      nextEntry = {
        ...nextEntry,
        completedDate: new Date().toISOString().slice(0, ISO_DATE_LENGTH),
      };
    } else if (patch.status && patch.status !== "complete") {
      nextEntry = { ...nextEntry, completedDate: null };
    }

    const hasExistingEntry = entries.some(
      (entry) => entry.teamRoleId === teamRoleId,
    );
    const next = hasExistingEntry
      ? entries.map((entry) =>
          entry.teamRoleId === teamRoleId ? nextEntry : entry,
        )
      : [...entries, nextEntry];

    setEntries(next);
    scheduleSave(next);
  };

  return (
    <div className={styles.page}>
      <div className={styles.pageContent}>
        <section className={styles.section}>
          <h2 className={styles.sectionHeading}>Review Status Dashboard</h2>
          <ReviewStatusDashboard rows={rows} projectEndDate={projectEndDate} />
        </section>
        <section className={styles.section}>
          <h2 className={styles.sectionHeading}>Team Review Tracker</h2>
          <TeamReviewTracker
            rows={rows}
            canEdit={canEdit}
            projectEndDate={projectEndDate}
            onEntryChange={handleEntryChange}
          />
        </section>
      </div>
    </div>
  );
};
